/**
 * PIN hashing. PBKDF2-SHA256 over WebCrypto, so the exact same code runs on the server
 * and on a phone with no connectivity — offline login is a hard requirement, which means
 * every registered device holds the hashes.
 *
 * Be honest about what this buys: a 6-digit PIN has a million candidates. At 210,000
 * iterations one guess takes about 55 ms on a desktop core, so a stolen device gives up one
 * user's PIN in at most fifteen hours on one core, an hour and a half on all 24 threads of a
 * Ryzen 9 5900X (measured, AUDITORIAS.md item 4), and less on a graphics card. That is
 * friction, not secrecy. The real boundary is physical possession of a registered device plus
 * the ability to revoke it, backed by the bitácora.
 *
 * Iteration count is stored inside the hash string, so it can be raised later without
 * invalidating anyone's existing PIN.
 */

const ITERATIONS = 210_000;
const KEY_BITS = 256;
const SALT_BYTES = 16;
const PREFIX = 'pbkdf2$sha256';

export const PIN_MIN_LENGTH = 6;
export const PIN_MAX_LENGTH = 10;

/**
 * Return type is inferred on purpose: this module is imported by the browser bundle and by
 * the Node server, and those two typings spell the WebCrypto interface differently. Naming
 * the DOM type here would force the API to load the DOM lib, which would then happily let
 * server code reference `document`.
 */
function getCrypto() {
  const c = globalThis.crypto;
  if (!c?.subtle) throw new ContextoInseguroError();
  return c;
}

/**
 * Thrown when the page is not in a secure context.
 *
 * Its own class because the UI has to be able to recognise it and say something useful. The
 * failure it describes is the single most confusing way to open this app: served over plain
 * http from a LAN address — which is exactly what somebody does when they move from their own
 * machine to testing on a phone — `crypto.subtle` is simply absent, so nobody can sign in and
 * nothing explains why. `localhost` counts as secure; `192.168.x.x` does not.
 */
export class ContextoInseguroError extends Error {
  constructor() {
    super(
      'Este navegador no puede cifrar el PIN porque la página no se abrió de forma segura. ' +
        'Use https, o localhost en esta misma computadora.',
    );
    this.name = 'ContextoInseguroError';
  }
}

/** True when PIN hashing and the service worker will actually run here. */
export function esContextoSeguro(): boolean {
  return Boolean(globalThis.crypto?.subtle);
}

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function isValidPinFormat(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_MIN_LENGTH},${PIN_MAX_LENGTH}}$`).test(pin);
}

/** Rejects the PINs people actually pick. Worth the friction exactly once, at setup. */
export function isWeakPin(pin: string): boolean {
  if (/^(\d)\1+$/.test(pin)) return true;
  const ascending = [...pin].every((c, i, a) => i === 0 || Number(c) === Number(a[i - 1]) + 1);
  const descending = [...pin].every((c, i, a) => i === 0 || Number(c) === Number(a[i - 1]) - 1);
  return ascending || descending;
}

/**
 * `Uint8Array<ArrayBuffer>` rather than a bare `Uint8Array`: since TypeScript 5.7 the element
 * type is generic over the backing buffer, and only an ArrayBuffer-backed view satisfies
 * WebCrypto's BufferSource. A SharedArrayBuffer-backed one would not.
 */
async function derive(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const crypto = getCrypto();
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    KEY_BITS,
  );
  return new Uint8Array(bits);
}

export async function hashPin(pin: string): Promise<string> {
  if (!isValidPinFormat(pin)) {
    throw new Error(`El PIN debe tener entre ${PIN_MIN_LENGTH} y ${PIN_MAX_LENGTH} dígitos`);
  }
  const salt = getCrypto().getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await derive(pin, salt, ITERATIONS);
  return `${PREFIX}$${ITERATIONS}$${toB64(salt)}$${toB64(hash)}`;
}

/** Constant-time comparison. The timing channel is tiny here, but it costs nothing to close. */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] as number) ^ (b[i] as number);
  return diff === 0;
}

export async function verifyPin(pin: string, stored: string): Promise<boolean> {
  const parts = stored.split('$');
  if (parts.length !== 5 || parts[0] !== 'pbkdf2' || parts[1] !== 'sha256') return false;
  const iterations = Number(parts[2]);
  if (!Number.isInteger(iterations) || iterations < 1_000) return false;
  try {
    const salt = fromB64(parts[3] as string);
    const expected = fromB64(parts[4] as string);
    const actual = await derive(pin, salt, iterations);
    return timingSafeEqual(actual, expected);
  } catch {
    return false;
  }
}

/** True when a stored hash was made with fewer iterations than we now use — rehash on next login. */
export function needsRehash(stored: string): boolean {
  const iterations = Number(stored.split('$')[2]);
  return !Number.isInteger(iterations) || iterations < ITERATIONS;
}
